import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {CompanyCreateSteps, CompanyCreateStepsLabels, CompanyCreateStepsList} from "./models/company-create-steps";
import {CompanyHttpService} from "./services/company-http.service";
import {Company, ICompany} from "./models/company";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {LegalType, LegalTypeLabels, LegalTypeList} from "./models/legal-type";
import {LegalEntityInformation} from "./models/legal-information";
import {LegalFormDescription, LegalFormList} from "./models/legal-form";
import {SalutationLabels, SalutationList} from "./models/salutation";
import {EditingServiceProvider} from "../../../_services/editing.service";
import {EditComponent} from "../../../_components/edit-component/edit-component";
import {AppRoutes} from "../../../enums/routes";
import {OwnerRoutes} from "../_models/_enums/owner-routes";
import {IShard, Shard} from "./models/shard";
import {InputFieldComponent} from "../../../_components/configuration/input-field/input-field.component";
import {SegmentComponent} from "../../../_components/configuration/segment/segment.component";
import {NgOptionComponent, NgSelectComponent} from "@ng-select/ng-select";
import {EditComponentBaseComponent} from "../../../_components/edit-component-base/edit-component-base.component";
import {StepDirective} from "../../../_directives/edit-component/step.directive";
import {FormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {NgSelectScrollAwareDirective} from "../../../_directives/ng-select-scroll-aware.directive";

@Component({
  selector: 'app-company-create',
  templateUrl: './company-create.component.html',
  imports: [
    InputFieldComponent,
    SegmentComponent,
    NgSelectComponent,
    NgOptionComponent,
    EditComponentBaseComponent,
    StepDirective,
    FormsModule,
    NgClass,
    // #236: MUST stay imported alongside the `[appendTo]`s in the template. The directive's selector is
    // `ng-select[appendTo]`, so it applies automatically — but only in a component that imports it, and a
    // missing import is silent (no compile error), leaving a portaled panel frozen while the page scrolls.
    NgSelectScrollAwareDirective
  ],
  styleUrl: './company-create.component.scss'
})
export class CompanyCreateComponent extends EditComponent<ICompany> implements OnInit {
  // protected current: ICompany | undefined | null = undefined;
  protected editing: boolean = false;
  protected readonly CompanyCreateStepsList = CompanyCreateStepsList;
  protected readonly CompanyCreateStepsLabels = CompanyCreateStepsLabels;
  protected currentStep: CompanyCreateSteps = CompanyCreateSteps.GeneralInformation;
  protected readonly CompanyCreateSteps = CompanyCreateSteps;
  protected readonly LegalTypeList = LegalTypeList;
  protected readonly LegalType = LegalType;
  protected readonly LegalTypeLabels = LegalTypeLabels;
  protected readonly parseInt = parseInt;
  protected readonly LegalEntityInformation = LegalEntityInformation;
  protected readonly LegalFormList = LegalFormList;
  protected readonly LegalFormDescription = LegalFormDescription;
  protected readonly SalutationList = SalutationList;
  protected readonly SalutationLabels = SalutationLabels;

  constructor(
    private provider: EditingServiceProvider,
    route: ActivatedRoute, router: Router, private http: CompanyHttpService, private gas: GlobalAlertService
  ) {
    super(router, route, provider.getService<ICompany>('ICompany'), () => new Company())
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params.step) {
        this.currentStep = CompanyCreateSteps[params.step as keyof typeof CompanyCreateSteps];
      }
      if (params.id && this.persistence.entity?.id !== params.id) {
        this.editing = true;
        this.http.load(params.id).withLoadingIndicator().subscribe({
          next: (company) => {
            this.persistence.edit(`/${AppRoutes.OwnerCompanies}`, true, company);
            if (!this.persistence.entity) throw new Error("Entity is undefined. This should never happen.");
            this.persistence.entity = Company.parse(this.persistence.entity);
          },
          error: (err) => {
            this.gas.createAndShow("Fehler", "Das Unternehmen konnte nicht geladen werden.", AlertLevel.error);
            console.error(err);
          }
        })
      } else if (!params.id) {
        this.editing = false;
        // Only seed a fresh entity the first time. queryParams emits again on every
        // tab/step switch (move() re-navigates) — re-running edit() here would call
        // the factory and replace the in-progress entity with an empty Company,
        // discarding everything the user has typed so far.
        if (!this.persistence.entity) {
          this.persistence.edit(`/${AppRoutes.OwnerCompanies}`, true);
        }
      }
    })
  }

  protected setCurrentStep(step: CompanyCreateSteps) {
    step = Math.max(Math.min(step, Math.pow(2, CompanyCreateStepsList.length - 1)), 1)
    this.move({
      step: CompanyCreateSteps[step]
    }).then();
  }

  protected cancel(): void {
    this.persistence.cancel();
  }

  protected save(): void {
    if (!this.persistence.isEntryPoint) {
      this.persistence.save();
      return;
    }

    if (!this.persistence.entity) {
      this.gas.createAndShow("Fehler", "Das Unternehmen konnte nicht gespeichert werden.", AlertLevel.error);
      console.error("Entity is undefined. This should never happen.");
      return;
    }

    this.http.upsert(this.persistence.entity).withLoadingIndicator().subscribe({
      next: company => {
        this.gas.createAndShow("Erfolgreich", "Das Unternehmen wurde erfolgreich gespeichert.", AlertLevel.success);
        this.persistence.entity = company;
        this.persistence.save();
      },
      error: err => {
        this.gas.createAndShow("Fehler", "Das Unternehmen konnte nicht gespeichert werden.", AlertLevel.error);
        console.error(err);
      }
    })
  }

  protected editShard(shard?: IShard) {
    let service = this.provider.getService<IShard>('IShard');
    let instance = service.getOrCreateInstance(this.id, () => new Shard());
    let copy = shard == undefined ? undefined : Shard.parse(shard);

    let observable = instance.edit(`/${AppRoutes.OwnerModule}/${OwnerRoutes.CompanyCreate}`, false, copy,
      {
        queryParams: {
          id: this.persistence.entity?.id,
          step: CompanyCreateSteps[this.currentStep]
        }
      }
    );

    this.persistence.subscribe(observable, edited => {
      if (!this.persistence.entity) {
        throw new Error("Entity is undefined. This should never happen.");
      }

      const shards = this.persistence.entity.shards;
      // Resolve which existing entry (if any) this edit replaces. Match the original
      // reference first: unsaved shards have no id, so matching by id alone would treat
      // an edit-then-save as a brand-new shard and push a duplicate. Fall back to id for
      // saved shards whose reference may differ from the array element.
      let index = shard ? shards.indexOf(shard) : -1;
      if (index === -1 && edited.id != undefined) {
        index = shards.findIndex(x => x.id === edited.id);
      }

      if (index !== -1) {
        shards[index] = edited;
      } else {
        shards.push(edited);
      }
    });


    let extras: NavigationExtras = {
      queryParams: {}
    };
    if (shard?.id) extras.queryParams!.id = shard.id;
    this.navigate(`/${AppRoutes.OwnerModule}/${OwnerRoutes.ShardEdit}`, extras).then()
  }
}
